import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Add, CalendarToday, Cancel, IosShare } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { LeaveRequest, LeaveBalance, LeaveRequestStatus, displayName } from '../../types';
import { apiService } from '../../services/api';
import { apiFetch } from '../../services/apiFetch';
import { useAuth } from '../../contexts/AuthContext';
import AddLeaveRequestModal from '../../components/AddLeaveRequestModal';
import LeaveCalendar from '../../components/LeaveCalendar';

const Leave: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([]);
  const [leaveBalances, setLeaveBalances] = useState<LeaveBalance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const exportIcal = (type: 'my' | 'team' | 'events') => {
    const fileMap = { my: 'my.ics', team: 'team.ics', events: 'events.ics' };
    const a = document.createElement('a');
    apiFetch(`/api/leave/calendar/${fileMap[type]}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        a.href = blobUrl;
        a.download = fileMap[type];
        a.click();
        URL.revokeObjectURL(blobUrl);
      })
      .catch((err) => alert(`Export failed: ${err?.message || 'Unknown error'}`));
  };

  // HR admins go straight to the management page
  useEffect(() => {
    if (user?.role === 'hr_admin' || user?.role === 'hr_manager') {
      navigate('/employees/leave', { replace: true });
    }
  }, [user, navigate]);

  const fetchLeaveData = async () => {
    if (!user?.employee) {
      setError('You need an employee profile to view leave information. HR admins should use the Employee Leave Management page.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');
      const [requests, balances] = await Promise.all([
        apiService.getLeaveRequests(),
        apiService.getMyLeaveBalances(),
      ]);
      setLeaveRequests(requests);
      setLeaveBalances(balances);

    } catch (err: any) {
      console.error('Error fetching leave data:', err);
      setError('Failed to load leave data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchLeaveDataEffect = async () => {
      // Check if user has an employee profile
      if (!user?.employee) {
        setError('You need an employee profile to view leave information. HR admins should use the Employee Leave Management page.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(''); // Clear any previous errors
        const [requests, balances] = await Promise.all([
          apiService.getLeaveRequests(),
          apiService.getMyLeaveBalances(),
        ]);
        setLeaveRequests(requests);
        setLeaveBalances(balances);
      } catch (err: any) {
        console.error('Error fetching leave data:', err);
        setError('Failed to load leave data');
      } finally {
        setLoading(false);
      }
    };

    if (user !== null) { // Only fetch when user is loaded
      fetchLeaveDataEffect();
    }
  }, [user]);

  const handleCancelRequest = async (requestId: string) => {
    try {
      await apiService.cancelLeaveRequest(requestId);
      await fetchLeaveData();
    } catch (err: any) {
      console.error('Error cancelling leave request:', err);
      setError(err.response?.data?.message || 'Failed to cancel leave request');
    }
  };

  const getStatusColor = (status: LeaveRequestStatus) => {
    switch (status) {
      case LeaveRequestStatus.APPROVED:
        return 'success';
      case LeaveRequestStatus.REJECTED:
        return 'error';
      case LeaveRequestStatus.PENDING:
        return 'warning';
      case LeaveRequestStatus.CANCELLED:
        return 'default';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Leave Management</Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<CalendarToday />}
            onClick={() => setIsCalendarOpen(true)}
          >
            View Calendar
          </Button>
          <Button
            variant="outlined"
            startIcon={<IosShare />}
            onClick={() => exportIcal('my')}
            title="Export your approved leave to Google Calendar / Outlook"
          >
            Export to Calendar
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setIsLeaveModalOpen(true)}
            disabled={!user?.employee}
          >
            Request Leave
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert 
          severity={!user?.employee ? "info" : "error"} 
          sx={{ mb: 2 }}
          action={
            !user?.employee && user?.role === 'hr_admin' ? (
              <Button 
                color="inherit" 
                size="small" 
                onClick={() => navigate('/employees/leave')}
              >
                Go to Employee Leave Management
              </Button>
            ) : undefined
          }
        >
          {error}
        </Alert>
      )}

      {/* Leave Balances */}
      <Typography variant="h6" gutterBottom>
        My Leave Balances
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {leaveBalances.map((balance) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={balance.id}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {balance.leaveType?.name}
                </Typography>
                <Typography variant="h4" color="primary">
                  {balance.closingBalanceDays}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  days available
                </Typography>
                <Box mt={1}>
                  <Typography variant="caption" display="block">
                    Used: {balance.usedDays} days
                  </Typography>
                  <Typography variant="caption" display="block">
                    Accrued: {balance.accruedDays} days
                  </Typography>
                  {balance.carryOverDays != null && balance.carryOverDays > 0 && (
                    <Box mt={0.5}>
                      <Typography variant="caption" display="block" color="info.main" fontWeight={600}>
                        🔄 {balance.carryOverDays}d carried over from last year
                      </Typography>
                      {balance.carryOverExpiry && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          Expires {new Date(balance.carryOverExpiry).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
        {leaveBalances.length === 0 && (
          <Grid size={12}>
            <Typography color="text.secondary">
              No leave balances found. Leave balances will appear once policies are configured.
            </Typography>
          </Grid>
        )}
      </Grid>

      {/* Leave Requests */}
      <Typography variant="h6" gutterBottom>
        My Leave Requests
      </Typography>
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Leave Type</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Days</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Details</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leaveRequests.map((request) => (
                <TableRow key={request.id} hover>
                  <TableCell>{request.leaveType?.name || '-'}</TableCell>
                  <TableCell>
                    {new Date(request.startDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {new Date(request.endDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell>{request.totalDays}</TableCell>
                  <TableCell>
                    <Chip
                      label={request.status.toUpperCase()}
                      color={getStatusColor(request.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {request.status === LeaveRequestStatus.REJECTED && request.approverComments ? (
                      <Tooltip title={request.approverComments} arrow>
                        <Typography variant="caption" color="error" sx={{ cursor: 'help', textDecoration: 'underline dotted' }}>
                          Rejected: {request.approverComments.length > 30 ? request.approverComments.substring(0, 30) + '...' : request.approverComments}
                        </Typography>
                      </Tooltip>
                    ) : request.status === LeaveRequestStatus.APPROVED ? (
                      <Typography variant="caption" color="text.secondary">
                        By {request.approver?.fullName || '-'}
                      </Typography>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {request.createdAt ? new Date(request.createdAt).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell>
                    {request.status === LeaveRequestStatus.PENDING && (
                      <Tooltip title="Cancel this request">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleCancelRequest(request.id)}
                        >
                          <Cancel fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {leaveRequests.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary">
                      No leave requests found. Click "Request Leave" to submit your first request.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <AddLeaveRequestModal
        open={isLeaveModalOpen}
        onClose={() => setIsLeaveModalOpen(false)}
        onLeaveRequestAdded={() => {
          fetchLeaveData();
        }}
      />

      <LeaveCalendar
        open={isCalendarOpen}
        onClose={() => setIsCalendarOpen(false)}
        leaveRequests={leaveRequests}
        employeeCountry={user?.employee?.country}
      />
    </Box>
  );
};

export default Leave;